import { useId, useMemo, useState } from 'react'

import { deployRequest } from '@/api/guardianforge/deploy'

export interface DecisionModalInitial {
  modelKey: string
  version: string
  format: string
  mlflowRunId?: string
  mlflowModelName?: string
  mlflowModelVersion?: string
  artifactPath?: string
  datasetId?: string
  watchId?: string
  cvatProjectId?: number
}

interface DecisionModalProps {
  comparisonId: string | null
  candidateSource: string
  candidateRef: string
  initial: DecisionModalInitial
  kind: 'approve' | 'reject'
  onClose: () => void
  onDone: () => void
}

export default function DecisionModal({
  comparisonId,
  candidateSource,
  candidateRef,
  initial,
  kind,
  onClose,
  onDone,
}: DecisionModalProps) {
  const titleId = useId()
  const warningId = useId()
  const modelKeyId = useId()
  const versionId = useId()
  const formatId = useId()
  const notesId = useId()
  const [notes, setNotes] = useState('')
  const [modelKey, setModelKey] = useState(initial.modelKey)
  const [version, setVersion] = useState(initial.version)
  const [format, setFormat] = useState(initial.format)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isApprove = kind === 'approve'
  const requiresNotes = isApprove && comparisonId === null

  const releasePayload = useMemo(() => {
    if (!isApprove) return null
    const payload: Record<string, unknown> = {
      model_key: modelKey.trim(),
      version: version.trim(),
      format: format.trim(),
    }
    addOptionalString(payload, 'mlflow_run_id', initial.mlflowRunId)
    addOptionalString(payload, 'mlflow_model_name', initial.mlflowModelName)
    addOptionalString(payload, 'mlflow_model_version', initial.mlflowModelVersion)
    addOptionalString(payload, 'artifact_path', initial.artifactPath)
    addOptionalString(payload, 'dataset_id', initial.datasetId)
    addOptionalString(payload, 'watch_id', initial.watchId)
    if (initial.cvatProjectId !== undefined) {
      payload.cvat_project_id = initial.cvatProjectId
    }
    return payload
  }, [format, initial, isApprove, modelKey, version])

  const canSubmit =
    !busy &&
    (!isApprove || Boolean(modelKey.trim() && version.trim() && format.trim())) &&
    (!requiresNotes || Boolean(notes.trim()))

  const submit = async () => {
    if (!canSubmit) return
    setBusy(true)
    setError(null)
    try {
      await deployRequest('/decisions', {
        method: 'POST',
        body: {
          ...(comparisonId === null ? {} : { comparison_id: comparisonId }),
          candidate_source: candidateSource,
          candidate_ref: candidateRef,
          decision: isApprove ? 'approved' : 'rejected',
          notes: notes.trim() || undefined,
          release_payload: releasePayload,
        },
      })
      onDone()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="deployment-modal-overlay" onClick={onClose}>
      <div
        className="deployment-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={requiresNotes ? warningId : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id={titleId}>{isApprove ? 'Approve & Register Release' : 'Reject Candidate'}</h3>
        {requiresNotes && (
          <div id={warningId} className="deployment-error-box">
            Comparator 결과 없이 승인·등록합니다. 검토 근거를 Notes에 반드시 남기세요.
          </div>
        )}
        {isApprove && (
          <div className="deployment-grid cols-2">
            <div>
              <label htmlFor={modelKeyId}>model_key</label>
              <input id={modelKeyId} value={modelKey} onChange={(event) => setModelKey(event.target.value)} />
            </div>
            <div>
              <label htmlFor={versionId}>version</label>
              <input id={versionId} value={version} onChange={(event) => setVersion(event.target.value)} />
            </div>
            <div>
              <label htmlFor={formatId}>format</label>
              <select id={formatId} value={format} onChange={(event) => setFormat(event.target.value)}>
                <option value="pt">pt</option>
                <option value="openvino_int8">openvino_int8</option>
                <option value="openvino_fp32">openvino_fp32</option>
                <option value="openvino_fp16">openvino_fp16</option>
              </select>
            </div>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <label htmlFor={notesId}>Notes {requiresNotes ? '(필수)' : isApprove ? '(선택)' : '(권장)'}</label>
          <textarea
            id={notesId}
            rows={3}
            value={notes}
            required={requiresNotes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={isApprove ? '검증 과정 메모...' : '기각 사유...'}
          />
        </div>
        {error && <div className="deployment-error-box">{error}</div>}
        <div className="deployment-flex" style={{ marginTop: 12 }}>
          <button className={isApprove ? 'primary' : 'danger'} disabled={!canSubmit} onClick={submit}>
            {busy ? '처리 중...' : isApprove ? 'Approve & Register' : 'Reject'}
          </button>
          <button className="ghost" onClick={onClose}>
            취소
          </button>
        </div>
      </div>
    </div>
  )
}

function addOptionalString(payload: Record<string, unknown>, key: string, value: string | undefined): void {
  if (value !== undefined && value !== '') {
    payload[key] = value
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message)
  }
  return String(error)
}
